import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { DRIVING_FAQ_CATEGORIES } from "@/content/driving-corpus/faq-defs";

export const Route = createFileRoute("/driving/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/driving-corpus");
    return { faqs: m.DRIVING_FAQ_TH };
  },
  component: DrivingFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ใบขับขี่ไทยและใบขับขี่สากล — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องใบอนุญาตขับรถไทย ชนิดชั่วคราว 2 ปีและ 5 ปี การต่ออายุและอบรม e-Learning ชาวต่างชาติขอใบขับขี่ไทย การใช้ใบขับขี่ต่างประเทศ ใบขับขี่สากลตามอนุสัญญาเจนีวา 1949 และเวียนนา 1968 การแปลรับรองใบขับขี่ ประกันภัยและบทลงโทษ",
        path: "/driving/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/driving/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ใบขับขี่ไทย–ใบขับขี่สากล FAQ", url: `${site}/driving/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/driving/faq")) },
      ],
    };
  },
});

function DrivingFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>ใบขับขี่ไทย–ใบขับขี่สากล FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ใบอนุญาตขับรถไทยและใบขับขี่สากล — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงกรมการขนส่งทางบก (dlt.go.th) พระราชบัญญัติรถยนต์ พ.ศ. 2522 พระราชบัญญัติจราจรทางบก พ.ศ. 2522
          กรมการกงสุล (consular.mfa.go.th) และอนุสัญญาว่าด้วยการจราจรทางถนน เจนีวา ค.ศ. 1949 กับเวียนนา ค.ศ. 1968
          ข้อมูลนี้เป็นข้อมูลทั่วไป ระเบียบและค่าธรรมเนียมอาจเปลี่ยนแปลงได้ โปรดตรวจสอบกับสำนักงานขนส่งก่อนดำเนินการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/driving/faq" className="text-primary underline">Thai driving licence &amp; IDP FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {DRIVING_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/driving/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามเอกสาร การแปลรับรองใบขับขี่ และการนัดคิวได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="driving" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
